import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "../../hooks/useAuth";

export default function ClientLogin() {
  const [isLogin, setIsLogin] = useState(true);
  const [formStatus, setFormStatus] = useState<"idle" | "submitting" | "error">("idle");
  const [errorMsg, setErrorMsg] = useState("");
  const { signIn, signUp } = useAuth();
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setFormStatus("submitting");
    setErrorMsg("");

    const form = e.currentTarget;
    const formData = new FormData(form);
    const email = formData.get("email") as string;
    const password = formData.get("password") as string;

    if (isLogin) {
      const { error } = await signIn(email, password);
      if (error) {
        setFormStatus("error");
        setErrorMsg(error.message || "Invalid email or password.");
      } else {
        navigate("/dashboard/client");
      }
    } else {
      const fullName = `${formData.get("firstName") as string} ${formData.get("lastName") as string}`.trim();
      const { error } = await signUp(email, password, fullName);
      if (error) {
        setFormStatus("error");
        setErrorMsg(error.message || "Something went wrong. Please try again.");
      } else {
        // Supabase may require email confirmation; guide user
        setFormStatus("idle");
        setIsLogin(true);
        setErrorMsg("Account created! Please check your email to confirm, then sign in.");
      }
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-soft-gray py-12 md:py-20 px-4">
      <div className="w-full max-w-md">
        {/* Logo */}
        <div className="text-center mb-8">
          <Link to="/" className="inline-flex items-center gap-2.5">
            <img
              src="https://static.readdy.ai/image/2f07940fa5d3a90c9fe64cfd1f3be31d/a5100fd298f183af8516edaf12c4c388.png"
              alt="Sycamore Oasis Corp"
              className="h-12 w-auto object-contain"
            />
            <div>
              <span className="text-lg font-semibold text-brand-green-800 leading-tight block">
                Sycamore Oasis
              </span>
              <span className="text-[10px] font-medium uppercase tracking-wider text-brand-green-600 block">
                Home Care Services
              </span>
            </div>
          </Link>
          <p className="text-sm text-gray-500 mt-3">
            {isLogin ? "Sign in to your client portal" : "Create your client account"}
          </p>
        </div>

        {/* Form Card */}
        <div className="bg-white rounded-2xl p-6 md:p-8 shadow-sm border border-gray-100">
          {/* Tabs */}
          <div className="flex p-1 bg-soft-gray rounded-full mb-6">
            <button
              onClick={() => { setIsLogin(true); setErrorMsg(""); setFormStatus("idle"); }}
              className={`flex-1 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                isLogin
                  ? "bg-white text-brand-green-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Sign In
            </button>
            <button
              onClick={() => { setIsLogin(false); setErrorMsg(""); setFormStatus("idle"); }}
              className={`flex-1 py-2 text-sm font-medium rounded-full transition-all duration-200 whitespace-nowrap ${
                !isLogin
                  ? "bg-white text-brand-green-700 shadow-sm"
                  : "text-gray-500 hover:text-gray-700"
              }`}
            >
              Register
            </button>
          </div>

          {errorMsg && (
            <div className={`rounded-lg p-3 border mb-4 ${errorMsg.includes("created") || errorMsg.includes("confirm")
              ? "bg-brand-green-50 border-brand-green-100"
              : "bg-red-50 border-red-100"
            }`}>
              <p className={`text-sm text-center ${errorMsg.includes("created") || errorMsg.includes("confirm")
                ? "text-brand-green-800"
                : "text-red-700"
              }`}>
                {errorMsg}
              </p>
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            {!isLogin && (
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label htmlFor="firstName" className="block text-xs font-medium text-gray-700 mb-1.5">
                    First Name
                  </label>
                  <input
                    type="text"
                    id="firstName"
                    name="firstName"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="Jane"
                  />
                </div>
                <div>
                  <label htmlFor="lastName" className="block text-xs font-medium text-gray-700 mb-1.5">
                    Last Name
                  </label>
                  <input
                    type="text"
                    id="lastName"
                    name="lastName"
                    required
                    className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                    placeholder="Doe"
                  />
                </div>
              </div>
            )}

            <div>
              <label htmlFor="email" className="block text-xs font-medium text-gray-700 mb-1.5">
                Email Address
              </label>
              <input
                type="email"
                id="email"
                name="email"
                required
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                placeholder="jane@example.com"
              />
            </div>

            <div>
              <label htmlFor="password" className="block text-xs font-medium text-gray-700 mb-1.5">
                Password
              </label>
              <input
                type="password"
                id="password"
                name="password"
                required
                minLength={6}
                className="w-full px-3 py-2.5 text-sm rounded-lg border border-gray-200 focus:border-brand-green-400 focus:ring-2 focus:ring-brand-green-100 outline-none transition-all"
                placeholder="••••••••"
              />
            </div>

            {isLogin && (
              <div className="flex items-center justify-between">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    name="remember"
                    className="w-4 h-4 rounded border-gray-300 text-brand-green-600 focus:ring-brand-green-500"
                  />
                  <span className="text-xs text-gray-600">Remember me</span>
                </label>
                <button type="button" className="text-xs font-medium text-brand-green-600 hover:text-brand-green-700">
                  Forgot password?
                </button>
              </div>
            )}

            <button
              type="submit"
              disabled={formStatus === "submitting"}
              className="w-full px-6 py-3 bg-brand-green-600 hover:bg-brand-green-700 disabled:bg-brand-green-400 text-white font-semibold rounded-lg transition-all duration-200 whitespace-nowrap"
            >
              {formStatus === "submitting"
                ? "Please wait..."
                : isLogin
                  ? "Sign In"
                  : "Create Account"}
            </button>
          </form>
        </div>

        {/* Back to site */}
        <p className="text-center text-sm text-gray-500 mt-6">
          <Link to="/" className="text-brand-green-600 hover:text-brand-green-700 font-medium">
            ← Back to website
          </Link>
        </p>
      </div>
    </div>
  );
}
